import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserHistory } from 'history';
import { matchPath } from 'react-router-dom';
import { isDev } from './devUtils';
import {
  hasExtensionReferenceInBreadCrumbs,
  hasExtensionReferenceInHintErrorStack,
  hasExtensionReferenceInEventErrorStack
} from './sentry.helper';

// List of pages that have the same pathname but different variants controlled with
// search params. Pages added to this list will have the search param added to the name
// during tracing (performance tracking)
const PAGES_WITH_VARIANTS = ['/onboarding/continue', '/'];

// List of react-router routes to be singled out for performance tracking
// Make sure the order of the routes is correct. The longest url under the same parent should be placed first and in decreasing order.
// eg. athlete/id/post should be added before athlete/id
const REACT_ROUTER_ROUTES = [
  { path: '/onboarding/continue' },
  { path: '/onboarding/google_registered' },
  { path: '/onboarding/suggested-friends' },
  { path: '/onboarding/facebook-friends' },
  { path: '/onboarding/welcome-to-strava' },
  { path: '/onboarding/subscription-trial' },
  { path: '/onboarding/direct-promotion' }
];

const history = createBrowserHistory();

if (!window.StravaSentryDsn) {
  window.StravaSentryDsn = {};
}
if (!window.StravaSentryDsn.deferred) {
  window.StravaSentryDsn.deferred = {};
  const promise = new Promise(
    function _(resolve, reject) {
      this.doResolve = resolve;
      this.doReject = reject;
    }.bind(window.StravaSentryDsn.deferred)
  );
  window.StravaSentryDsn.deferred.then = promise.then.bind(promise);
}

const { enabled, environment, release, userId, dsn, sampleRate, debug } =
  window.StravaSentry || {};

window.StravaSentryDsn.deferred.doResolve(dsn);

window.StravaSentryDsn.deferred.then((_dsn) => {
  // configure sentry_frontend.yml to enable sentry on local
  if (enabled) {
    Sentry.init({
      dsn: _dsn,
      maxBreadcrumbs: 50,
      integrations: [
        new BrowserTracing({
          tracingOrigins: [
            'https://www.staging.strava.com',
            'https://www.strava.com',
            'localhost',
            /^\//
          ],
          beforeNavigate: (context) => {
            const { pathname, search } = window.location;
            const includeSearchParam = PAGES_WITH_VARIANTS.includes(pathname);
            return {
              ...context,
              name: includeSearchParam ? pathname.concat(search) : pathname
            };
          }
        })
      ],
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i
      ],
      tracesSampleRate: sampleRate,
      debug,
      attachStacktrace: true,
      release,
      initialScope: {
        user: { id: userId }
      },
      environment,
      beforeSend: (event, hint) => {
        if (
          hasExtensionReferenceInHintErrorStack(hint) ||
          hasExtensionReferenceInBreadCrumbs(event) ||
          hasExtensionReferenceInEventErrorStack(event) ||
          hint?.originalException === 'Timeout' // google-recaptcha flakiness
        ) {
          return null;
        }
        return event;
      },
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(
        history,
        REACT_ROUTER_ROUTES,
        matchPath
      )
    });
  }
});

/** @type {import("@sentry/browser").withScope} */
export const withScope = (...args) => Sentry.withScope(...args);

/** @type {import("@sentry/browser").captureException} */
export const logError = (error, captureContext) => {
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.error('logError :::', error, captureContext);
    return;
  }

  Sentry.captureException(error, captureContext);
};

/** @type {import("@sentry/browser").captureMessage} */
export const logMessage = (message, captureContext) => {
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.error('logMessage :::', message, captureContext);
    return;
  }

  Sentry.captureMessage(message, captureContext);
};

export default { logError, logMessage, withScope };
